import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import Cookies from 'universal-cookie';
import PropTypes from 'prop-types';

import { client } from '../command/auth';
import { selector_auth } from '../reducer/auth';
import { invalidateUserCache } from '../command/userCache';
import { pushUserDataToDataLayer } from '../utils/pushUserDataToDataLayer';

export const Auth0Context = React.createContext({});
export const useAuth0 = () => useContext(Auth0Context);

export const Auth0Provider = ({ children }) => {
  const auth = useSelector(selector_auth);
  const { isAuthenticated, isLoading, role, token, user } = auth;
  
  // Track if we've already pushed data to prevent multiple calls
  const hasTrackedRef = useRef(false);
  const trackedUserIdRef = useRef(null);

  // Push user data to dataLayer only once per authenticated session
  useEffect(() => {
    if (isAuthenticated && user && !isLoading) {
      // Only push if we haven't tracked this user yet, or if it's a different user
      if (!hasTrackedRef.current || trackedUserIdRef.current !== user.id) {
        pushUserDataToDataLayer(user, 'userDataLoaded');
        hasTrackedRef.current = true;
        trackedUserIdRef.current = user.id;
      }
    } else if (!isAuthenticated) {
      // Reset tracking when user logs out
      hasTrackedRef.current = false;
      trackedUserIdRef.current = null;
    }
  }, [isAuthenticated, user?.id, isLoading]);

  const loginWithRedirect = useCallback(() => {
    client.loginWithRedirect({
      appState: { targetUrl: window.location.pathname },
    });
  }, []);

  // Opens one Auth0 connection (e.g. 'google-oauth2') without the Universal
  // Login page; the return trip is the same as loginWithRedirect.
  const loginWithConnection = useCallback((connection) => {
    client.loginWithRedirect({
      appState: { targetUrl: window.location.pathname },
      connection,
    });
  }, []);

  const logout = useCallback(() => {
    invalidateUserCache();

    const cookies = new Cookies();
    cookies.remove('auth0_token', { path: '/' });

    let kp = window.freedomeConfig.knownPages;
    if (
      ![kp.profile, kp.wishList, kp.orderList].includes(
        window.location.pathname
      )
    ) {
      cookies.set('target_url', window.location.pathname, { path: '/' });
    }

    client.logout({ returnTo: window.location.origin });
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      role,
      token,
      user,
      loginWithRedirect: loginWithRedirect,
      loginWithConnection: loginWithConnection,
      logout: logout,
    }),
    [auth, loginWithRedirect, loginWithConnection, logout]
  );

  return (
    <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>
  );
};
Auth0Provider.defaultProps = { children: null };
Auth0Provider.propTypes = { children: PropTypes.node };
